import axios from 'axios';
import store from '@/store'

const initialState = () => ({
  fetching: null,
  error: null,
  calendars: [],
  success: null,
  teacherSurveys: [],
  recordings: [],
  attendance: []
});

export default {
  namespaced: true,
  state: initialState(),
  mutations: {
    RESET_STATE(state){
      state.fetching = null
      state.error = null
      state.calendars = []
      state.success = null
      state.teacherSurveys = []
      state.recordings = []
      state.attendance = []
    },
    FETCHING(state, value){
      state.fetching = !!value;
    },
    FETCH_SUCCESS(state, value){
      state.success = !!value;
    },
    FETCH_ERROR(state, value){
      state.error = value;
    },
    SET_CALENDAR_LIST(state, values){
      state.calendars = values;
    },
    SET_RECORDINGS(state, payload) {
      state.recordings = payload
    },
    SET_ATTENDANCE(state, payload) {
      state.attendance = payload
    },
    UPDATE_ATTENDANCE(state, classNumber) {
      state.attendance.contactos_presentes.push({
        clase: classNumber,
      })
    },
    UPSERT_RECORDING_RATE(state, args) {
      const npsList = state.attendance.nps_clases;

      const index = npsList.findIndex(item =>
        Number(item.clase) === Number(args.classNumber)
      );

      if (index !== -1) {
        const existing = npsList[index];

        npsList.splice(index, 1, {
          ...existing,
          valoracion: args.rate,
          comentario: args.comment
        });
      } else {
        npsList.push({
          calendario_id: args.calendarId,
          contacto_id: args.userId,
          clase: args.classNumber,
          valoracion: args.rate,
          comentario: args.comment
        });
      }
    },
    SET_TEACHER_SURVEYS(state, payload) {
      state.teacherSurveys = payload
    },
  },
  actions: {
    async fetch({ commit, rootState }, args){
      commit('RESET_STATE');
      commit('FETCHING', true);

      const params = {
        pais_id: rootState.Init.country.pais_id,
        sin_completos: 1,
        cursos_ids: args.course
      }

      if(args.start){
        params['fecha_desde'] = args.start
      }

      return await axios
        .get(process.env.VUE_APP_EDUCACIONIT_API_SERVER + "public/calendarios", {params})
        .then(response => {
          commit('FETCH_SUCCESS', true);
          commit('FETCHING', false);
          commit('SET_CALENDAR_LIST', response.data);
          return response;
        })
        .catch(error => {
          commit('FETCH_SUCCESS', false);
          commit('FETCHING', false);

          if(404 === error.response.status){
            commit('SET_CALENDAR_LIST', []);
            return error.response;
          }

          commit('FETCH_ERROR', error);

          return error.response;
        });
    },
    async reset({ commit }){
      commit('RESET_STATE');
    },
    async getTeacherSurveys({ commit, state }, args) {
      const options = {
        headers:{
          'Authorization': `Bearer ${await store.state.AuthFirebase.auth.currentUser?.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      };

      const params = {
        calendario_id: args.calendar_id
      }

      try {
        const response = await axios.get(
          process.env.VUE_APP_EDUCACIONIT_API_SERVER + 'contactos/encuestas',
          {
            ...options,
            params
          }
        );

        let total = 0;

        const surveysWithAvg = response.data.data.map((survey) => {
          const values = [
            survey.instructor_experiencia,
            survey.instructor_ejemplos,
            survey.instructor_rendimiento,
            survey.instructor_puntualidad
          ];

          const validValues = values.filter((v) => v !== null && v !== undefined);

          const count = validValues.length;

          const avg = count > 0
            ? Number((validValues.reduce((acc, val) => acc + val, 0) / count).toFixed(2))
            : 0;

          total += avg;

          return {
            ...survey,
            avg
          };
        });

        const myAvg = surveysWithAvg.length > 0
          ? Number((total / surveysWithAvg.length).toFixed(2))
          : 0;

        const result = {
          my_avg: myAvg,
          total_students: 0,
          survey_completed: surveysWithAvg.length,
          surveys: surveysWithAvg
        };

        commit('SET_TEACHER_SURVEYS', result)
        return result;
      } catch (err) {
        commit('SET_TEACHER_SURVEYS', [])
        return [];
      }
    },
    async getRecordings({ commit, dispatch }, args) {
      const datePerClass = new Map(
        args.calendar.clases.map((c) => [Number(c.clase), c.fecha_inicio]),
      );

      try {
        const recordings = await dispatch(
          'services/api/plataformas/recording/list',
          { calendario_id: args.calendar.calendario_id },
          { root: true },
        );

        /**
         * Recordings don't carry the class date, so the schedule stays this join against
         * the calendar the page already holds.
         * A row without `playback_url` cannot be authorized, so it is dropped instead of rendering an unplayable section.
         */
        const recordingsMap = recordings
          .filter((recording) => !!recording.playback_url)
          .reduce((map, { recording_id, class_number }) => {
            if (!map.has(class_number)) {
              map.set(class_number, {
                date: datePerClass.get(Number(class_number)),
                parts: [],
              });
            }

            map.get(class_number).parts.push({ recording_id });
            return map;
          }, new Map());

        // No client-side sort, the list already arrives ordered by class_number, start_time, recording_id.
        const mapped = Array.from(recordingsMap, ([class_number, { date, parts }]) => ({
          class_number,
          date,
          parts,
        }));

        commit('SET_RECORDINGS', mapped)
        return mapped;
      } catch (e) {
        commit('SET_RECORDINGS', [])
        return [];
      }
    },
    async getAttendance({ commit, state }, args) {
      const options = {
        headers: {
          Authorization: `Bearer ${await store.state.AuthFirebase.auth.currentUser?.getIdToken()}`,
          'Content-Type': 'application/json',
        },
      };

      const params = {
        contacto_id: args.user_id,
        calendario_id: args.calendar_id,
        include: 'contactos_presentes,nps_clases',
        attributes: 'calendario_id'
      }

      try {
        const response = await axios.get(
          process.env.VUE_APP_EDUCACIONIT_API_SERVER + 'alumni/contactos-calendarios',
          {
            ...options,
            params
          }
        );

        commit('SET_ATTENDANCE', response.data.data[0])
        return response.data.data[0];
      } catch (err) {
        commit('SET_ATTENDANCE', [])
        return [];
      }
    },
    async markAttendance({ commit, state }, args) {
      const options = {
        headers:{
          'Authorization': `Bearer ${await store.state.AuthFirebase.auth.currentUser?.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      };

      const params = {
        clase: args.classNumber,
        presente_tipo_id: 'R'
      }

      try {
        const response = await axios.post(
          process.env.VUE_APP_EDUCACIONIT_API_SERVER + `contactos/${args.userId}/calendarios/${args.calendarId}/presentes`,
          params,
          options
        );

        commit('UPDATE_ATTENDANCE', args.classNumber)

        return response.data
      } catch (err) {
        console.error(err);
      }
    },
    async rateRecording({ commit, state }, args) {
      const options = {
        headers:{
          'Authorization': `Bearer ${await store.state.AuthFirebase.auth.currentUser?.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      };

      const params = {
        valoracion: args.rate
      }

      if (args.comment && args.comment.trim() !== '') {
        params.comentario = args.comment;
      }

      const response = await axios.post(
        process.env.VUE_APP_EDUCACIONIT_API_SERVER + `contactos/${args.userId}/calendarios/${args.calendarId}/clases/${args.classNumber}/nps`,
        params,
        options
      );

      commit('UPSERT_RECORDING_RATE', args)

      return response.data;
    }
  },
  getters: {
    fetching: (state) => state.fetching,
    error: (state) => state.error,
    calendars: (state) => state.calendars,
    success: (state) => state.success,
    teacherSurveys: (state) => state.teacherSurveys,
    recordings: (state) => state.recordings,
    attendance: (state) => state.attendance
  },
};
